import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { renderSubmittedValue } from '@nxsys/utils/dateUtils';
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { IMPORT_COLUMNS_WIDTH } from '../constants';
import AppButton from '@nxsys/components/AppButton';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import UploadedFileInformation from './UploadedFileInformation';
import { toNumber } from 'lodash';

type ViewAgencyUploadFileProps = {
  isOpen: boolean;
  toggle: () => void;
  data: IAgencyUpload | null;
  title: string;
  onImport?: () => void;
};

const ViewAgencyUploadFile: React.FC<ViewAgencyUploadFileProps> = ({
  isOpen,
  toggle,
  data,
  title,
  onImport,
}) => {
  const attachmentUrl = useMemo(() => data?.attachmentURL, [data]);

  const { messages } = useTypedIntl();
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [columns, rowsData] = useMemo(() => {
    if (!excelData.length) {
      return [[], []];
    }
    const headers = excelData[0] || [];
    const columns = headers.map((h: string, index: number) => ({
      key: index,
      title: h,
      dataIndex: 'row',
      render: (rowData: any) => (
        <span className={`mb-0`}>
          {renderSubmittedValue(h, rowData?.[index])}
        </span>
      ),
      width:
        IMPORT_COLUMNS_WIDTH[h as keyof typeof IMPORT_COLUMNS_WIDTH] ?? 200,
    }));
    const data = excelData.slice(1).map((row, index) => ({ id: index, row }));
    return [columns, data];
  }, [excelData]);
  const totalRecords = useMemo(() => rowsData.length, [rowsData]);

  const handleFetchFromUrl = async (url: string) => {
    try {
      setIsFetchingData(true);
      const response = await fetch(url);
      const data = await response.arrayBuffer();
      const workbook = XLSX.read(new Uint8Array(data), { type: 'buffer' });
      const sheet = workbook.Sheets['Sheet1'];
      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        blankrows: false,
        raw: true,
        rawNumbers: true,
      });
      setExcelData(Object.values(jsonData || {}));
    } catch (error) {
      console.error('Error fetching or parsing Excel file:', error);
    } finally {
      setIsFetchingData(false);
    }
  };

  useEffect(() => {
    if (attachmentUrl) handleFetchFromUrl(attachmentUrl);
  }, [attachmentUrl]);

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title={title}
      width="90%"
      minHeight="200px"
    >
      <div className="flex justify-between mb-4">
        <UploadedFileInformation
          fileName={data?.fileName}
          fileSize={toNumber(data?.fileSize)}
          totalRecords={totalRecords}
        />
        <AppButton
          name={messages['common.import']}
          disabled={data?.status === 'CANCELED'}
          onClick={() => {
            onImport?.();
            toggle();
          }}
        />
      </div>
      <AppTableContainer
        data={[...rowsData]}
        loading={isFetchingData}
        page={0}
        total={totalRecords}
        pageSize={totalRecords}
        columns={columns}
        scroll={{ x: columns?.length ? 180 * columns?.length : 0, y: 400 }}
        {...(totalRecords > 1000 ? { virtual: true } : {})}
      />
    </AppModalContainer>
  );
};

export default ViewAgencyUploadFile;
